/**
 * Nerds list — DataTable enterprise.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Download, HardHat, Star, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/page-header";
import { NerdStatusPill } from "@/components/status-pill";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { nerdsApi, type Nerd } from "@/lib/api-cadastros";
import { bulkApi } from "@/lib/api-bulk";
import { downloadCsv } from "@/lib/api-system";
import { formatCPF } from "@/lib/format";

export function NerdsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [bulkConfirm, setBulkConfirm] = useState<{ ids: string[] } | null>(null);
  const list = useQuery({ queryKey: ["nerds"], queryFn: () => nerdsApi.list({ size: 200 }) });

  const bulkDelete = useMutation({
    mutationFn: (ids: string[]) => bulkApi.deleteNerds(ids),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["nerds"] });
      toast.success(`${result.deleted} técnico(s) excluído(s)`);
      setBulkConfirm(null);
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const columns: ColumnDef<Nerd>[] = [
    {
      id: "legal_name",
      accessorKey: "display_name",
      header: "Técnico",
      cell: ({ row }) => {
        const n = row.original;
        const name = n.display_name ?? n.legal_name ?? n.user_name ?? n.user_email ?? "Sem nome";
        return (
          <div className="flex items-center gap-3 min-w-0">
            <Avatar size="md">
              <AvatarFallback name={name} />
            </Avatar>
            <div className="min-w-0">
              <p className="font-medium truncate">{name}</p>
              <p className="text-xs text-muted-foreground truncate">
                {n.user_email ? n.user_email : (
                  <span className="font-mono">{formatCPF(n.cpf) ?? n.cnpj ?? "—"}</span>
                )}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <NerdStatusPill status={row.original.status} />,
    },
    {
      id: "rank_score",
      accessorKey: "rank_score",
      header: "Rank",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
          <span className="font-semibold tabular-nums">{row.original.rank_score}</span>
          <span className="text-xs text-muted-foreground">/100</span>
        </div>
      ),
    },
    {
      id: "completed_jobs",
      accessorKey: "completed_jobs",
      header: "Jobs concluídos",
      cell: ({ row }) => (
        <span className="tabular-nums">{row.original.completed_jobs}</span>
      ),
    },
    {
      id: "working_radius_km",
      accessorKey: "working_radius_km",
      header: "Raio",
      cell: ({ row }) => (
        <Badge variant="default">{row.original.working_radius_km} km</Badge>
      ),
    },
    {
      id: "cidade",
      header: "Cidade",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.address?.cidade?.name ?? "—"}
          {row.original.address?.cidade?.uf ? `/${row.original.address.cidade.uf}` : ""}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Técnicos (Nerds)"
        description="Profissionais cadastrados na plataforma."
        actions={
          <Button variant="outline" onClick={() => downloadCsv("nerds")}>
            <Download className="h-4 w-4" /> Exportar
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        searchPlaceholder="Buscar por nome, CPF, cidade..."
        emptyTitle="Nenhum técnico cadastrado"
        emptyDescription="Os técnicos serão cadastrados via onboarding ou cadastro manual."
        emptyAction={null}
        onRowClick={(n) => navigate(`/nerds/${n.id}`)}
        enableSelection
        getRowId={(n) => n.id}
        bulkActions={(rows) => (
          <Button
            size="sm"
            variant="outline"
            className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
            onClick={() => setBulkConfirm({ ids: rows.map((r) => r.id) })}
          >
            <Trash2 className="h-3.5 w-3.5" /> Excluir selecionados
          </Button>
        )}
        columnLabels={{
          legal_name: "Técnico",
          status: "Status",
          rank_score: "Rank",
          completed_jobs: "Jobs",
          working_radius_km: "Raio",
          cidade: "Cidade",
        }}
      />

      <ConfirmDialog
        open={!!bulkConfirm}
        onOpenChange={(v) => !v && setBulkConfirm(null)}
        variant="danger"
        title={`Excluir ${bulkConfirm?.ids.length ?? 0} técnico(s)?`}
        description="Esta ação não pode ser desfeita."
        confirmLabel="Sim, excluir"
        loading={bulkDelete.isPending}
        onConfirm={() => bulkConfirm && bulkDelete.mutate(bulkConfirm.ids)}
      />

      {list.data && list.data.items.length === 0 && (
        <div className="mt-4">
          <HardHat className="h-5 w-5 text-muted-foreground" />
        </div>
      )}
    </>
  );
}
